import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@material-ui/core'

const trDay = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul' }).format(d)
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('tr-TR') : '-')

/**
 * Bring one old sale into the billing lane.
 *
 * The date is the whole decision here, so it is the only field. These payments
 * predate the columns that record when a sale happened, which is exactly why
 * they are invisible to the automatic pass: the system does not know their
 * date and must not invent one.
 *
 * A suggestion is offered and its provenance is shown, because "this is the
 * confirmed timestamp" and "this is when checkout started, which is close" are
 * different claims and only one of them is the taxable event.
 */
export default function AdmitDialog({ row, open, onClose, onConfirm, busy, error }) {
  const today = trDay(new Date())
  const [orderDate, setOrderDate] = useState('')

  React.useEffect(() => {
    if (open) setOrderDate(row?.suggested_order_date || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row?.payment_pk])

  if (!row) return null

  const guessed = row.suggested_source === 'created_at'

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Geçmiş satışı faturaya al</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{row.organisation || row.person_email}</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {row.amount} {row.currency}
            {row.charged_try ? ` · tahsil edilen ${row.charged_try} TL` : ''} · {row.payment_id}
          </Typography>
        </Stack>

        {/*
          Said before the field, not after it. An operator who has already typed
          a date is not going to re-read a caption telling them the value was a
          guess.
        */}
        {guessed ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bu satışın kesin tarihi sistemde yok. Aşağıdaki tarih, ödemenin başlatıldığı an ({day(row.created_at)}) baz
            alınarak önerildi; satışın gerçekleştiği tarih bu olmayabilir. Doğrulayıp gerekiyorsa düzeltin.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Satış tarihi kayıtlı: {day(row.confirmed_at)}.
          </Alert>
        )}

        <TextField
          label="Sipariş tarihi (satış)"
          type="date"
          fullWidth
          size="small"
          value={orderDate}
          onChange={(e) => setOrderDate(e.target.value)}
          // Upper bound only. The point of this screen is sales that are months
          // old, so a lower bound would rule out the only rows that reach it.
          inputProps={{ max: today }}
          InputLabelProps={{ shrink: true }}
          helperText='Belgede "Sipariş Tarihi" olarak görünür. Fatura bugünün tarihiyle kesilir.'
        />

        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
          Bu işlem fatura kesmez. Satış, onay bekleyen listesine düşer; belge yalnızca siz “Kes” dedikten sonra
          oluşturulur.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} disabled={busy} sx={{ mr: 1 }}>
          Vazgeç
        </Button>
        <Button variant="contained" onClick={() => onConfirm(orderDate)} disabled={busy || !orderDate}>
          {busy ? 'Alınıyor…' : 'Faturaya al'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

AdmitDialog.propTypes = {
  row: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  busy: PropTypes.bool,
  error: PropTypes.string,
}
